<template>
  <b-overlay :show="loading" no-center variant="transparent" rounded="sm">
    <template #overlay>
      <loading-spinner />
    </template>
    <b-row>
      <b-col
        cols="12"
        md="9"
        lg="6"
        xl="6"
      >
        <b-alert
          v-if="cameraPermissionDenied"
          variant="warning"
          :show="cameraPermissionDenied"
        >
          <div class="alert-body">
            {{ $t('Please grant camera permission in the browser') }}
          </div>
        </b-alert>
        <b-alert
          v-else
          variant="info"
          :show="!cameraPermissionDenied"
        >
          <div class="alert-body">
            {{ $t('Only 1 face should appear in the camera') }}
          </div>
        </b-alert>
        <div class="cam-container">
          <video
            ref="video"
            autoplay
            playsinline
          />
          <canvas ref="canvas" />
        </div>
        <b-button
          variant="primary"
          size="sm"
          class="mt-1"
          block
          :disabled="faces.length < 1 || loading"
          @click="submit"
        >
          <b-spinner
            v-if="loading"
            small
          />
          {{ $t('Send') }}
        </b-button>
      </b-col>
    </b-row>
  </b-overlay>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'
import * as tf from '@tensorflow/tfjs'
import * as faceDetection from '@tensorflow-models/face-detection'
import '@tensorflow/tfjs-backend-webgl'

import {
  BAlert,
  BButton,
  BCol,
  BOverlay,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import {
  showNotify,
  throwDefaultError,
} from '@/helpers/helpers'

export default {
  components: {
    BAlert,
    BButton,
    BCol,
    BOverlay,
    BRow,
    BSpinner,
  },
  data() {
    return {
      base64ImageType: 'image/png',
      cameraPermissionDenied: false,
      canvas: null,
      context: null,
      faces: [],
      loading: false,
      model: null,
      video: null,
    }
  },
  async mounted() {
    await this.loadModel()
    await this.initializeCamera()
  },
  methods: {
    async detectFaces() {
      if (this.model) {
        // Yüzleri tespit etmek için döngü
        const detect = async () => {
          this.faces = await this.model.estimateFaces(this.video, {
            flipHorizontal: false,
          })

          // Yüz algılama işlemini sürekli devam ettir
          requestAnimationFrame(detect)
        }
        detect()
      }
    },
    async initializeCamera() {
      this.video = this.$refs.video
      this.canvas = this.$refs.canvas
      this.context = this.canvas.getContext('2d')

      try {
        // Kameraya erişim isteği
        this.video.srcObject = await navigator.mediaDevices.getUserMedia({
          video: true,
        })

        // Kamera başladığında yüz algılamaya başla
        this.video.onloadedmetadata = () => {
          this.detectFaces()
        }
      } catch (error) {
        // Eğer kullanıcı izni reddederse veya bir hata oluşursa
        this.cameraPermissionDenied = true
      }
    },
    async loadModel() {
      this.loading = true
      // Yüz algılama modelini yükleyin
      this.model = await faceDetection.createDetector(
        faceDetection.SupportedModels.MediaPipeFaceDetector,
        {
          runtime: 'tfjs',
        },
      )
      this.loading = false
    },
    submit() {
      if (Array.isArray(this.faces) && this.faces.length > 0 && this.video) {
        this.loading = true

        try {
          if (this.video && this.context) {
            const canvas = document.createElement('canvas')
            const context = canvas.getContext('2d')
            canvas.width = this.video.videoWidth
            canvas.height = this.video.videoHeight
            context.drawImage(this.video, 0, 0, canvas.width, canvas.height)
            const base64Data = canvas.toDataURL(this.base64ImageType)

            useJwt.faceDetection({
              mime_type: this.base64ImageType,
              image: base64Data,
            })
              .then(() => {
                showNotify(this.$toast, this.$t('Process successful'), 'success')
              })
              .catch(() => {
                throwDefaultError(this.$toast)
              })
              .finally(() => {
                this.loading = false
              })
          }
        } catch (error) {
          throwDefaultError(this.$toast)
        }
      }
    },
  },
}
</script>

<style scoped>
.cam-container {
  position: relative;
  width: 640px;
  height: 480px;
  max-width: 100%;
  max-height: 100%;
  margin-top: 16px;
  background-color: #1b1c22;
  border-radius: 12px;
}
video, canvas {
  position: absolute;
  object-fit: contain !important;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}
</style>
